import { Injectable } from '@angular/core';

/**
 * A literal character that should render as markup wherever it appears in stored content.
 *
 * Game data is authored with the character itself — it stays readable in the admin editor, in a
 * diff, and in the source rulebook text — and is swapped for markup at render time. That avoids
 * hand-writing an image tag at every occurrence, and means changing the artwork is a stylesheet
 * edit rather than a data migration.
 */
export interface ParseIconsGlyph {
    /** Markup emitted for a single occurrence. */
    html: string;
    /**
     * When set, a run of consecutive occurrences is wrapped in one element with this class, rather
     * than emitted as loose siblings.
     */
    groupClass?: string;
    /**
     * When set, a wrapped run carries `aria-label="<groupLabel> <count>"`. Without it a run of four
     * icons is announced four times over, or — if the markup is a bare decorative element — not at
     * all.
     */
    groupLabel?: string;
}

/**
 * A named icon whose appearance is controlled by CSS rather than a raw image path.
 *
 * Use this when the icon is a monochrome SVG that should inherit `currentColor`, needs to work
 * in both Angular templates and data-driven `[innerHTML]` contexts, or may eventually need a
 * click handler (the parent wraps the emitted span as required).
 *
 * Template placeholders: `{sizeClass}` → e.g. `smallIcon`; `{alt}` → aria-label text.
 */
export interface ParseIconsIconDef {
    html: string;
}

@Injectable({ providedIn: 'root' })
export class ParseIconsConfig {
    iconsFolder: string;
    /**
     * Glyph substitutions, keyed by gameId then by the character itself. Keyed by game because one
     * app can host several, and a character meaningful in one game's rules is ordinary text in
     * another's.
     */
    glyphs?: { [gameId: string]: { [glyph: string]: ParseIconsGlyph } };
    /** Per-icon HTML overrides, keyed by gameId then by icon name (as used in [[name|...]]). */
    icons?: { [gameId: string]: { [iconName: string]: ParseIconsIconDef } };
}
